import { zodResolver } from "@hookform/resolvers/zod";
import { BedIcon, PlusCircleIcon, TrashIcon } from "@phosphor-icons/react";
import { times } from "lodash-es";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useIntl } from "react-intl";
import { object } from "zod";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button/Button";
import { Card } from "@/components/Card/Card";
import { CounterButton } from "@/components/CounterButton/CounterButton";
import { IconButton } from "@/components/IconButton/IconButton";
import { Select, SelectItem } from "@/components/Select";
import { TravellerTypeSelectionItem } from "@/components/TravellerTypeSelectionItem/TravellerTypeSelectionItem";
import type { TRoom } from "@/features/hotels/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_VISITORS_PER_ROOM,
} from "@/features/hotels/utils/constants";
import {
  getRoomsValidationSchema,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";
import { MIN_ADULT_AGE } from "@/utils/constants";

const newRoom: TRoom = {
  adults: 1,
  children: 0,
  childrenAges: [],
};

type Props = {
  value: HotelSearchFormValues["rooms"];
  onConfirm: (values: HotelSearchFormValues["rooms"]) => void;
};

export function VisitorsSelection({ value, onConfirm }: Props) {
  const intl = useIntl();

  const methods = useForm<Pick<HotelSearchFormValues, "rooms">>({
    defaultValues: {
      rooms: value,
    },
    resolver: zodResolver(
      object({
        rooms: getRoomsValidationSchema(intl),
      }),
    ),
  });

  const { handleSubmit, control } = methods;

  const {
    fields: rooms,
    remove,
    update,
    append,
  } = useFieldArray({
    control,
    name: "rooms",
  });

  const isMultiRoomBookingEnabled = useFeatureIsOn("multi-room-booking");

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={async (event) => {
          event.stopPropagation();

          await handleSubmit((data) => {
            onConfirm(data.rooms);
          })(event);

          // scroll to first error, if any
          event.target
            .querySelector("[aria-invalid='true']")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        className="flex w-full basis-full flex-col"
      >
        <div className="mb-3 text-sm text-text-primary">
          {intl.formatMessage(
            {
              id: "hotels.search.guestsPerRoomInformation",
              defaultMessage:
                "Maximum number of guests per room is {maxGuestsPerRoom}",
            },
            {
              maxGuestsPerRoom: MAX_VISITORS_PER_ROOM,
            },
          )}
        </div>

        <div className="space-y-3">
          {rooms.map((room, roomIndex) => {
            const maxChildCount = Math.min(
              MAX_VISITORS_PER_ROOM - (room.adults ?? 1),
              MAX_CHILDREN_PER_ROOM,
            );
            const currentChildCount = room.childrenAges?.length ?? 0;
            const maxAdultCount = MAX_VISITORS_PER_ROOM - currentChildCount;

            return (
              <Card
                key={room.id}
                icon={
                  <BedIcon
                    size={24}
                    color="var(--color-surface-pressed-accent-strong)"
                  />
                }
                title={
                  isMultiRoomBookingEnabled
                    ? `${intl.formatMessage({ id: "hotels.search.room", defaultMessage: "Room" })} ${roomIndex + 1}`
                    : intl.formatMessage({
                        id: "hotels.search.room",
                        defaultMessage: "Room",
                      })
                }
                action={
                  rooms.length > 1 ? (
                    <IconButton
                      aria-label={intl.formatMessage({
                        id: "remove",
                        defaultMessage: "Remove",
                      })}
                      color="error"
                      onClick={() => remove(roomIndex)}
                      disablePadding
                    >
                      <TrashIcon size={24} />
                    </IconButton>
                  ) : null
                }
              >
                <div className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
                  <TravellerTypeSelectionItem
                    label={intl.formatMessage({
                      id: "hotels.search.adult",
                      defaultMessage: "Adult",
                    })}
                    description={intl.formatMessage({
                      id: "hotels.search.twelveYearOldPlus",
                      defaultMessage: "12+ years old",
                    })}
                  >
                    <CounterButton
                      value={room.adults ?? 1}
                      onChange={(value) =>
                        update(roomIndex, {
                          ...room,
                          adults: value,
                        })
                      }
                      minValue={1}
                      maxValue={maxAdultCount}
                      minusButtonAriaLabel={intl.formatMessage({
                        id: "hotels.search.removeAdult",
                        defaultMessage: "Remove adult",
                      })}
                      plusButtonAriaLabel={intl.formatMessage({
                        id: "hotels.search.addAdult",
                        defaultMessage: "Add adult",
                      })}
                    />
                  </TravellerTypeSelectionItem>

                  <div>
                    <TravellerTypeSelectionItem
                      label={intl.formatMessage({
                        id: "hotels.search.child",
                        defaultMessage: "Child",
                      })}
                      description={intl.formatMessage({
                        id: "hotels.search.underTwelveYearOld",
                        defaultMessage: "Under 12 years old",
                      })}
                    >
                      <CounterButton
                        value={currentChildCount}
                        onChange={(value) => {
                          const nextAges = Array.from(
                            { length: value },
                            (_, i) => room.childrenAges?.[i],
                          );
                          update(roomIndex, {
                            ...room,
                            children: value,
                            childrenAges: nextAges,
                          });
                        }}
                        maxValue={maxChildCount}
                        minusButtonAriaLabel={intl.formatMessage({
                          id: "hotels.search.removeChild",
                          defaultMessage: "Remove child",
                        })}
                        plusButtonAriaLabel={intl.formatMessage({
                          id: "hotels.search.addChild",
                          defaultMessage: "Add child",
                        })}
                      />
                    </TravellerTypeSelectionItem>

                    {/* Children ages */}
                    <div className="grid grid-cols-2 gap-2 not-empty:mt-4">
                      {times(currentChildCount, (childIndex) => {
                        return (
                          <Controller
                            key={childIndex}
                            control={control}
                            name={`rooms.${roomIndex}.childrenAges.${childIndex}`}
                            render={({ field, fieldState }) => (
                              <Select
                                label={intl.formatMessage(
                                  {
                                    id: "hotels.search.childNAge",
                                    defaultMessage: "Child {number} age",
                                  },
                                  {
                                    number: childIndex + 1,
                                  },
                                )}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  field.onChange(Number(e.target.value));
                                }}
                              >
                                {times(MIN_ADULT_AGE, (i) => (
                                  <SelectItem key={i} value={i}>
                                    {i === 0
                                      ? intl.formatMessage({
                                          id: "hotels.search.underOneYearOld",
                                          defaultMessage: "Under 1",
                                        })
                                      : i}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {isMultiRoomBookingEnabled ? (
          <Button
            type="button"
            variant="text"
            startIcon={<PlusCircleIcon size={24} />}
            onClick={(e) => {
              append(newRoom);
              e.currentTarget.blur();
            }}
          >
            {intl.formatMessage({
              id: "hotels.search.addRoom",
              defaultMessage: "Add another room",
            })}
          </Button>
        ) : null}
        <div className="mt-auto">
          <Button fullWidth type="submit" variant="contained">
            {intl.formatMessage({ id: "confirm", defaultMessage: "Confirm" })}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
